import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { AuthProvider as OidcAuthProvider, useAuth as useOidcAuth } from 'react-oidc-context';
import { authDisabled, oidcSettings, resolveIssuer } from './oidcConfig';
import { clearHub, getHub, hubFromIssuer, setHub } from './hubStore';
import { HubLogin } from './HubLogin';
import { OlAuthContext, type OlAuth, type OlAuthUser } from './OlAuthContext';
import { setAccessToken, setUnauthorizedHandler } from './tokenStore';

/** Decode a JWT payload without verifying (display/role-hint use ONLY — the API is the enforcer). */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Realm roles from the first source that carries a `realm_access` claim. Keycloak's DEFAULT client
 * scope puts realm_access in the ACCESS token, not the ID token/userinfo — so the access-token
 * claims are checked first. Returns null when NO source carries the claim (roles UNKNOWN — the
 * visual gates must then fail OPEN; the API is the real enforcer).
 */
function extractRoles(
  ...sources: Array<Record<string, unknown> | null | undefined>
): string[] | null {
  for (const src of sources) {
    if (!src) continue;
    const realm = (src['realm_access'] as { roles?: string[] } | undefined)?.roles;
    if (Array.isArray(realm)) return realm;
  }
  return null;
}

/** Bridges react-oidc-context to the app's OlAuth shape and wires token + reject-closed handler. */
function RealAuthBridge({ children }: { children: ReactNode }) {
  const oidc = useOidcAuth();

  const value = useMemo<OlAuth>(() => {
    const profile = oidc.user?.profile as Record<string, unknown> | undefined;
    const accessClaims = oidc.user?.access_token
      ? decodeJwtPayload(oidc.user.access_token)
      : null;
    const roles = extractRoles(accessClaims, profile);
    const user: OlAuthUser | null =
      oidc.isAuthenticated && profile
        ? {
            name:
              (profile.name as string) ||
              (profile.preferred_username as string) ||
              (profile.email as string) ||
              'User',
            email: profile.email as string | undefined,
            sub: profile.sub as string | undefined,
            roles: roles ?? [],
            rolesKnown: roles !== null,
          }
        : null;
    const status: OlAuth['status'] = oidc.isLoading
      ? 'loading'
      : oidc.error
        ? 'error'
        : oidc.isAuthenticated
          ? 'authenticated'
          : 'unauthenticated';
    return {
      status,
      isAuthenticated: oidc.isAuthenticated,
      user,
      error: oidc.error?.message,
      login: () => void oidc.signinRedirect(),
      logout: () => {
        clearHub(); // return to hub selection after sign-out (allows switching hubs)
        void oidc.signoutRedirect();
      },
      mock: false,
    };
  }, [oidc]);

  useEffect(() => {
    setAccessToken(oidc.user?.access_token ?? null);
  }, [oidc.user]);

  useEffect(() => {
    // Reject-closed: a 401 from the product API forces a fresh sign-in.
    setUnauthorizedHandler(() => void oidc.signinRedirect());
    return () => setUnauthorizedHandler(null);
  }, [oidc]);

  return <OlAuthContext.Provider value={value}>{children}</OlAuthContext.Provider>;
}

/** Dev-standalone session — no redirect, no token. MUST NOT be used in a deployed build. */
function MockAuthProvider({ children }: { children: ReactNode }) {
  const value = useMemo<OlAuth>(
    () => ({
      status: 'authenticated',
      isAuthenticated: true,
      user: {
        name: 'Dev Operator',
        email: 'dev@onelattice.local',
        sub: 'mock-user',
        // The full tenant role catalogue (docs/architecture/iam-rbac-uam-implementation.md §2), so
        // every role-gated surface is reachable in VITE_AUTH_DISABLED dev. Excludes platform_admin
        // (platform-plane, never provisioned in v1) and hub_user (zero-grant floor role).
        roles: ['hub_admin', 'hub_operator', 'flow_designer', 'flow_approver', 'analyst', 'auditor'],
        rolesKnown: true,
      },
      login: () => {},
      logout: () => {},
      mock: true,
    }),
    [],
  );
  useEffect(() => {
    setAccessToken(null);
  }, []);
  return <OlAuthContext.Provider value={value}>{children}</OlAuthContext.Provider>;
}

/**
 * Top-level auth provider. Three modes:
 *  - VITE_AUTH_DISABLED=true → a mock session (local dev / CI standalone) — unchanged.
 *  - real auth, issuer resolvable (full VITE_OIDC_ISSUER, or a stored hub slug + issuer base) →
 *    OIDC-PKCE against that realm.
 *  - real auth, no hub chosen yet → the hub-slug login screen, then remount against the derived
 *    realm (ProtectedRoute then redirects to the IdP).
 */
export function OneLatticeAuthProvider({ children }: { children: ReactNode }) {
  const [, forceHub] = useState(0);

  if (authDisabled()) {
    return <MockAuthProvider>{children}</MockAuthProvider>;
  }

  const issuer = resolveIssuer();
  if (!issuer) {
    return (
      <HubLogin
        onSubmit={(slug) => {
          setHub(slug);
          forceHub((n) => n + 1);
        }}
      />
    );
  }

  // Explicit-issuer mode (VITE_OIDC_ISSUER set, HubLogin skipped): derive the hub slug from the
  // issuer's realm name (`…/realms/opsflow-<slug>`) so `{hub}`-scoped API paths target the REAL
  // tenant, never a placeholder. A non-convention realm leaves the hub unset — tenantHub() then
  // surfaces an explicit configuration error instead of silently calling the wrong tenant.
  if (!getHub()) {
    const derived = hubFromIssuer(issuer);
    if (derived) {
      setHub(derived);
    }
  }

  return (
    <OidcAuthProvider {...oidcSettings(issuer)}>
      <RealAuthBridge>{children}</RealAuthBridge>
    </OidcAuthProvider>
  );
}
